import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  GraduationCap, Plus, Sparkles, CheckCircle2, BookOpen, Loader2, X, Award,
} from "lucide-react";
import { toast } from "sonner";

function Markdown({ text }) {
  const lines = (text || "").split("\n");
  return (
    <div className="space-y-2 text-sm leading-relaxed text-white/85">
      {lines.map((ln, i) => {
        const t = ln.trim();
        if (!t) return null;
        if (t.startsWith("### ")) return <h4 key={i} className="font-heading font-semibold text-base text-white mt-4">{t.slice(4)}</h4>;
        if (t.startsWith("## ")) return <h3 key={i} className="font-heading font-bold text-lg text-white mt-5">{t.slice(3)}</h3>;
        if (t.startsWith("- ")) return <li key={i} className="ml-4 list-disc">{inline(t.slice(2))}</li>;
        return <p key={i}>{inline(t)}</p>;
      })}
    </div>
  );
  function inline(s) {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, j) =>
      p.startsWith("**") && p.endsWith("**")
        ? <strong key={j} className="text-white font-semibold">{p.slice(2, -2)}</strong>
        : <span key={j}>{p}</span>
    );
  }
}

function ModuleViewer({ module, onClose, onDone, isConsultant }) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const quiz = module.quiz || [];

  const score = () => {
    if (quiz.length === 0) return 100;
    const correct = quiz.filter((q, i) => answers[i] === q.answer_index).length;
    return Math.round((correct / quiz.length) * 100);
  };

  const finish = async () => {
    setSaving(true);
    try {
      const sc = score();
      const { data } = await api.post(`/modules/${module.id}/complete`, { score: sc });
      toast.success(`Módulo concluído! +${data.points_awarded} pontos (${sc}%)`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="module-viewer">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-sm bg-brand-surface border border-brand-border">
        <div className="sticky top-0 bg-brand-surface/95 backdrop-blur border-b border-brand-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <span className="text-xs text-brand-orange font-bold uppercase tracking-wider">{module.category}</span>
            <h2 className="font-heading font-bold text-lg">{module.title}</h2>
          </div>
          <button onClick={onClose} className="text-brand-muted hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6">
          <Markdown text={module.content} />

          {quiz.length > 0 && (
            <div className="mt-8 pt-6 border-t border-brand-border">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2 mb-4">
                <Award size={18} className="text-brand-orange" /> Quiz
              </h3>
              <div className="space-y-5">
                {quiz.map((q, i) => (
                  <div key={i}>
                    <p className="text-sm font-medium mb-2">{i + 1}. {q.question}</p>
                    <div className="grid gap-2">
                      {q.options.map((opt, oi) => {
                        const chosen = answers[i] === oi;
                        const isCorrect = submitted && oi === q.answer_index;
                        const isWrong = submitted && chosen && oi !== q.answer_index;
                        return (
                          <button key={oi} disabled={submitted}
                            onClick={() => setAnswers({ ...answers, [i]: oi })}
                            data-testid={`quiz-${i}-opt-${oi}`}
                            className={`text-left text-sm px-3 py-2 rounded-sm border transition-colors ${
                              isCorrect ? "border-brand-success bg-brand-success/15 text-brand-success" :
                              isWrong ? "border-destructive bg-destructive/15 text-destructive" :
                              chosen ? "border-brand-blue bg-brand-blue/15" : "border-brand-border hover:border-brand-blue/40"
                            }`}>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-brand-surface/95 backdrop-blur border-t border-brand-border px-6 py-4 flex justify-end gap-2">
          {module.completed ? (
            <span className="flex items-center gap-2 text-sm text-brand-success font-medium"><CheckCircle2 size={16} /> Já concluído</span>
          ) : !isConsultant ? (
            <span className="text-sm text-brand-muted">Módulo para consultores</span>
          ) : quiz.length > 0 && !submitted ? (
            <Button onClick={() => setSubmitted(true)} disabled={Object.keys(answers).length < quiz.length}
              data-testid="submit-quiz-button" className="bg-brand-blue hover:bg-brand-bluehover rounded-sm font-bold">
              Verificar respostas
            </Button>
          ) : (
            <Button onClick={finish} disabled={saving} data-testid="finish-module-button"
              className="bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Concluir módulo (${score()}%)`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Academy() {
  const { user, refresh } = useAuth();
  const [modules, setModules] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("Auto-educação");
  const [generating, setGenerating] = useState(false);

  const canManage = user.role === "admin" || user.role === "agency_manager";
  const isConsultant = user.role === "consultant";

  const load = () => api.get("/modules").then((r) => setModules(r.data));
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      await api.post("/modules/generate", { topic, category, points_reward: 150 });
      toast.success("Módulo gerado por IA! ✨");
      setAiOpen(false); setTopic("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar módulo");
    } finally { setGenerating(false); }
  };

  if (!modules) return <div className="h-96 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-blue" /></div>;

  return (
    <div className="space-y-6" data-testid="academy-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2">
            <GraduationCap className="text-brand-blue" /> Academia
          </h1>
          <p className="text-brand-muted text-sm mt-1">Auto-educação para consultores de excelência. Conclui módulos e ganha pontos.</p>
        </div>
        {canManage && (
          <Dialog open={aiOpen} onOpenChange={setAiOpen}>
            <DialogTrigger asChild>
              <Button data-testid="ai-generate-button" className="bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold">
                <Sparkles size={16} className="mr-1" /> Gerar com IA
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-brand-surface border-brand-border">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-brand-orange" /> Gerar módulo com IA</DialogTitle></DialogHeader>
              <form onSubmit={generate} className="space-y-4">
                <div>
                  <Label>Tema</Label>
                  <Input value={topic} onChange={(e) => setTopic(e.target.value)} required
                    placeholder="Ex: Técnicas de fecho de venda" data-testid="ai-topic"
                    className="mt-1 bg-background border-brand-border rounded-sm" />
                </div>
                <div>
                  <Label>Categoria</Label>
                  <Input value={category} onChange={(e) => setCategory(e.target.value)}
                    className="mt-1 bg-background border-brand-border rounded-sm" />
                </div>
                <p className="text-xs text-brand-muted">A IA cria um artigo completo + quiz de 4 perguntas. Pode demorar alguns segundos.</p>
                <DialogFooter>
                  <Button type="submit" disabled={generating} data-testid="ai-generate-submit"
                    className="bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold">
                    {generating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> A gerar...</> : "Gerar módulo"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map((m) => (
          <div key={m.id} data-testid="module-card"
            className={`p-5 rounded-sm border flex flex-col transition-all ${m.completed ? "border-brand-success/40 bg-brand-success/5" : "border-brand-border bg-brand-surface hover:border-brand-blue/40"}`}>
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-sm bg-brand-blue/15 text-brand-blue flex items-center justify-center">
                <BookOpen size={18} />
              </div>
              {m.ai_generated && (
                <span className="text-xs px-2 py-0.5 rounded-sm bg-brand-orange/15 text-brand-orange flex items-center gap-1">
                  <Sparkles size={11} /> IA
                </span>
              )}
            </div>
            <h3 className="font-heading font-semibold mt-3">{m.title}</h3>
            <p className="text-sm text-brand-muted mt-1 leading-relaxed flex-1">{m.description}</p>
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-brand-muted">{m.category} · {(m.quiz || []).length} perguntas</span>
              <span className="text-sm font-bold text-brand-orange">+{m.points_reward}</span>
            </div>
            <Button onClick={() => setViewing(m)} data-testid="open-module-button"
              className={`w-full mt-4 rounded-sm font-bold ${m.completed ? "bg-brand-border text-white hover:bg-brand-border/70" : "bg-brand-blue hover:bg-brand-bluehover"}`}>
              {m.completed ? <><CheckCircle2 size={15} className="mr-1" /> Rever</> : "Começar"}
            </Button>
          </div>
        ))}
        {modules.length === 0 && (
          <div className="col-span-full text-center py-16 text-brand-muted">
            <GraduationCap size={40} className="mx-auto mb-3 opacity-40" /> Ainda não há módulos.
          </div>
        )}
      </div>

      {viewing && (
        <ModuleViewer module={viewing} isConsultant={isConsultant}
          onClose={() => setViewing(null)} onDone={async () => { await load(); await refresh(); }} />
      )}
    </div>
  );
}
